"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useState } from "react";
import { useTranslations } from "next-intl";
import { getMyBudgetCue, type MyBudgetCue } from "@/app/actions/my-usage-actions";
import { computeCapStatus } from "@/lib/usage/my-usage";
import { formatCurrencyUSD } from "@/lib/format";
import { cn } from "@/lib/utils";
const usd = (value: number) => formatCurrencyUSD(value, {
  maxFractionDigits: 2
});

// The dropdown content unmounts on close, so cache the fetch at module level:
// the first open pays one query, later opens render instantly with no flash.
// Failed fetches clear the cache so the next open retries.
let cuePromise: Promise<MyBudgetCue | null> | null = null;
function loadCue(): Promise<MyBudgetCue | null> {
  cuePromise ??= getMyBudgetCue().then(result => {
    if (!result.success) {
      cuePromise = null;
      return null;
    }
    return result.data;
  }, () => {
    cuePromise = null;
    return null;
  });
  return cuePromise;
}

/**
 * Quiet spend-cap cue for the sidebar user menu (AIR-643). Renders one muted
 * "$X of $Y" line — amber once spend crosses 80% of the cap — and renders
 * NOTHING when the user has no cap, while loading, or on error. Informational
 * only: never nags, warns, or blocks.
 */
export function BudgetCue() {
  const $ = _c(25);
  const t = useTranslations("budgetCue");
  const [cue, setCue] = useState(null);
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => {
      let cancelled = false;
      loadCue().then(data => {
        if (!cancelled) {
          setCue(data);
        }
      });
      return () => {
        cancelled = true;
      };
    };
    t1 = [];
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  useEffect(t0, t1);
  if (!cue) {
    return null;
  }
  let t2;
  if ($[2] !== cue.limitUsd || $[3] !== cue.spendUsd) {
    t2 = computeCapStatus(cue.spendUsd, cue.limitUsd);
    $[2] = cue.limitUsd;
    $[3] = cue.spendUsd;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const cap = t2;
  let t3;
  if ($[5] !== cue.limitUsd || $[6] !== cue.period || $[7] !== cue.spendUsd || $[8] !== t) {
    t3 = cue.period === "monthly" ? t("spendOfCapThisMonth", {
      spent: usd(cue.spendUsd),
      cap: usd(cue.limitUsd)
    }) : cue.period === "daily" ? t("spendOfCapToday", {
      spent: usd(cue.spendUsd),
      cap: usd(cue.limitUsd)
    }) : t("spendOfCap", {
      spent: usd(cue.spendUsd),
      cap: usd(cue.limitUsd)
    });
    $[5] = cue.limitUsd;
    $[6] = cue.period;
    $[7] = cue.spendUsd;
    $[8] = t;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const label = t3;
  const t4 = cap.isNearCap ? "text-amber-600 dark:text-amber-400" : "text-muted-foreground";
  let t5;
  if ($[10] !== t4) {
    t5 = cn("flex items-center gap-2 text-xs", t4);
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const t6 = cap.isNearCap ? "bg-amber-500" : "bg-muted-foreground/50";
  let t7;
  if ($[12] !== t6) {
    t7 = cn("absolute inset-y-0 left-0 rounded-full", t6);
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  const t8 = `${cap.percent}%`;
  let t9;
  if ($[14] !== t8) {
    t9 = {
      width: t8
    };
    $[14] = t8;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== t7 || $[17] !== t9) {
    t10 = <span aria-hidden="true" className="relative h-1 w-16 shrink-0 overflow-hidden rounded-full bg-muted"><span className={t7} style={t9} /></span>;
    $[16] = t7;
    $[17] = t9;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== label) {
    t11 = <span className="truncate">{label}</span>;
    $[19] = label;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] !== t10 || $[22] !== t11 || $[23] !== t5) {
    t12 = <div className="px-4 pb-3"><div className={t5}>{t10}{t11}</div></div>;
    $[21] = t10;
    $[22] = t11;
    $[23] = t5;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  return t12;
}